import React, { useState, useEffect } from 'react';
import { Car, Users, MessageCircle, TrendingUp, Building2, MapPin } from 'lucide-react';
import ActiveDrivers from '../components/driver/ActiveDrivers';
import DriverMessages from '../components/driver/DriverMessages';
import HotspotRecommendations from '../components/driver/HotspotRecommendations';
import CompanySelector from '../components/company/CompanySelector';
import TripManagement from '../components/driver/TripManagement';
// Phase 5 Components
import GPSTracker from '../components/driver/GPSTracker';
import NotificationSystem from '../components/notifications/NotificationSystem';
import AdvancedAnalytics from '../components/analytics/AdvancedAnalytics';

const DriverDashboard = () => {
  const [driverStatus, setDriverStatus] = useState('offline');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    tripsToday: 0,
    earningsToday: 0,
    onlineTimeMinutes: 0,
    rating: 4.8
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [activeCompany, setActiveCompany] = useState(null);
  const [showCompanySelector, setShowCompanySelector] = useState(false);

  // Get user ID from token or context
  const getUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    return null;
  };

  const userId = getUserId();

  useEffect(() => {
    const savedStatus = localStorage.getItem('driverStatus') || 'offline';
    setDriverStatus(savedStatus);
    
    const handleStorageChange = (e) => {
      if (e.key === 'driverStatus') {
        setDriverStatus(e.newValue || 'offline');
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Load driver statistics
  useEffect(() => {
    const loadStats = async () => {
      setIsLoadingStats(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

  const response = await fetch('http://localhost:3002/api/auth/stats', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const result = await response.json();
        
        if (result.success) {
          setStats(result.data);
        } else {
          console.error('Stats loading failed:', result.message);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadStats();
  }, []);

  const updateStatus = (newStatus) => {
    const newStatus2 = driverStatus === 'online' ? 'offline' : 'online';
    localStorage.setItem('driverStatus', newStatus2);
    setDriverStatus(newStatus2);
    
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'driverStatus',
      newValue: newStatus2,
      storageArea: localStorage
    }));
  };

  const handleShareLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        console.log('Position shared:', position.coords);
        alert('Position erfolgreich geteilt!');
      });
    }
  };

  const formatOnlineTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatEarnings = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Car className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Fahrer Dashboard</h1>
              <p className="text-sm text-gray-600">Wo finde ich Kunden? - Aktuelle Informationen und Empfehlungen</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Phase 5: Notification System */}
            <NotificationSystem />
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              driverStatus === 'online' 
                ? 'bg-green-100 text-green-800' 
                : driverStatus === 'busy'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {driverStatus === 'online' ? '✅ Online' : 
               driverStatus === 'busy' ? '⏸️ Beschäftigt' : '🔴 Offline'}
            </div>
            <div className="text-sm text-gray-600">
              Letztes Update: {new Date().toLocaleTimeString('de-DE')}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <Car className="mr-2 h-5 w-5" />
              Dashboard
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <TrendingUp className="mr-2 h-5 w-5" />
              Analytics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('trips')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'trips'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <MapPin className="mr-2 h-5 w-5" />
              Yolculuklar
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <>
          {/* Phase 5: GPS Tracker */}
          <GPSTracker />
          
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <Users className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Aktive Fahrer</h3>
                  <p className="text-sm opacity-90">Sieh wer gerade online ist</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Hotspot Empfehlungen</h3>
                  <p className="text-sm opacity-90">Beste Orte für Kunden</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <MessageCircle className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Fahrer Chat</h3>
                  <p className="text-sm opacity-90">Kommunikation mit Kollegen</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-lg cursor-pointer hover:from-orange-600 hover:to-orange-700 transition-colors"
              onClick={() => setShowCompanySelector(!showCompanySelector)}
            >
              <div className="flex items-center">
                <Building2 className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Şirket Seçimi</h3>
                  <p className="text-sm opacity-90">
                    {activeCompany ? activeCompany.name : 'Şirket seçiniz'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Selector */}
          {showCompanySelector && userId && (
            <CompanySelector 
              driverId={userId}
              onCompanySelect={(company) => {
                setActiveCompany(company);
                setShowCompanySelector(false);
              }}
            />
          )}

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-8">
              <ActiveDrivers />
              <DriverMessages />
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <HotspotRecommendations />
              
              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🚀 Schnelle Aktionen</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => updateStatus('online')}
                    className={`p-4 rounded-lg transition-colors ${
                      driverStatus === 'online' 
                        ? 'bg-gray-100 border border-gray-300' 
                        : 'bg-green-50 border border-green-200 hover:bg-green-100'
                    }`}
                    disabled={driverStatus === 'online'}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">🟢</div>
                      <div className={`font-medium ${driverStatus === 'online' ? 'text-gray-600' : 'text-green-800'}`}>
                        {driverStatus === 'online' ? 'Bereits Online' : 'Verfügbar setzen'}
                      </div>
                      <div className={`text-sm ${driverStatus === 'online' ? 'text-gray-500' : 'text-green-600'}`}>
                        Status: {driverStatus === 'online' ? 'Online' : 'Bereit für Fahrten'}
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => updateStatus('offline')}
                    className={`p-4 rounded-lg transition-colors ${
                      driverStatus === 'offline' 
                        ? 'bg-gray-100 border border-gray-300' 
                        : 'bg-red-50 border border-red-200 hover:bg-red-100'
                    }`}
                    disabled={driverStatus === 'offline'}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">🔴</div>
                      <div className={`font-medium ${driverStatus === 'offline' ? 'text-gray-600' : 'text-red-800'}`}>
                        {driverStatus === 'offline' ? 'Bereits Offline' : 'Offline gehen'}
                      </div>
                      <div className={`text-sm ${driverStatus === 'offline' ? 'text-gray-500' : 'text-red-600'}`}>
                        Status: {driverStatus === 'offline' ? 'Inaktiv' : 'Nicht aktiv'}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Heute's Übersicht</h3>
                {isLoadingStats ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Lade Statistiken...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{stats.tripsToday}</div>
                      <div className="text-sm text-gray-600">Fahrten heute</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{formatEarnings(stats.earningsToday)}</div>
                      <div className="text-sm text-gray-600">Verdienst heute</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{formatOnlineTime(stats.onlineTimeMinutes)}</div>
                      <div className="text-sm text-gray-600">Online Zeit</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">{stats.rating.toFixed(1)}⭐</div>
                      <div className="text-sm text-gray-600">Durchschnittsbewertung</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Analytics Tab - Phase 5 */}
      {activeTab === 'analytics' && (
        <AdvancedAnalytics />
      )}

      {/* Trips Tab */}
      {activeTab === 'trips' && userId && (
        <TripManagement driverId={userId} />
      )}
    </div>
  );
};

export default DriverDashboard;
